import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  ReleaseTemplateSummary,
  ReleaseTemplateVersion,
} from "../../shared/types/api";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  LoadingState,
  Select,
  StatusBadge,
  Tabs,
  type TabDefinition,
} from "../../shared/ui";
import { TemplateContentDrawer } from "./TemplateContentDrawer";
import {
  VERSION_STATUS_LABELS,
  VERSION_STATUS_TONE,
  describeImpact,
  describeVersionContents,
} from "./templateVocabulary";

type ContentKind =
  | "prerequisites" | "approvals" | "execution-steps"
  | "rollback-steps" | "validation-checks"
  | "test-requirements" | "document-requirements";

/**
 * One template's versions, and what each contains (§56, §57).
 *
 * <p>Version-first rather than a wizard. §57 asks for a stepped builder, and this is the same idea
 * in the shape this application already uses: the version is picked at the top and its contents
 * are tabs, so somebody comparing V2 against V3 does not have to walk a wizard twice.
 *
 * <p>An activated version is shown read-only with a lock, and the way forward is "New draft" —
 * because §52 means the alternative does not exist, and a disabled edit button that never explains
 * itself sends people looking for a permission problem.
 */
export function TemplateVersionDrawer({
  template,
  onClose,
}: {
  template: ReleaseTemplateSummary | null;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<ContentKind>("prerequisites");
  const [adding, setAdding] = useState<ContentKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const queryClient = useQueryClient();

  const versions = useQuery({
    queryKey: ["release-template-versions", template?.id],
    queryFn: () =>
      api.get<ReleaseTemplateVersion[]>(
        `/api/v1/release-templates/${template!.id}/versions`,
      ),
    enabled: template != null,
  });

  useEffect(() => {
    if (versions.data && versions.data.length > 0 && selectedId == null) {
      // Open on the draft if there is one, since that is the only thing anybody can change.
      const draft = versions.data.find((version) => version.status === "DRAFT");
      setSelectedId((draft ?? versions.data[0])!.id);
    }
  }, [versions.data, selectedId]);

  useEffect(() => {
    if (template == null) {
      setSelectedId(null);
      setError(null);
    }
  }, [template]);

  const [settings, setSettings] = useState({
    changeSummary: "",
    rollbackRequired: false,
    observationMinutes: "",
  });

  const selected = versions.data?.find((entry) => entry.id === selectedId) ?? null;
  useEffect(() => {
    if (!selected) {
      return;
    }
    setSettings({
      changeSummary: selected.changeSummary ?? "",
      rollbackRequired: selected.rollbackRequired,
      observationMinutes:
        selected.observationMinutes == null ? "" : String(selected.observationMinutes),
    });
  }, [selected]);

  if (template == null) {
    return null;
  }

  const version = versions.data?.find((entry) => entry.id === selectedId) ?? null;
  const editable = version?.status === "DRAFT" && can("RELEASE_TEMPLATE_MANAGE");

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["release-template-versions", template.id] });
    void queryClient.invalidateQueries({ queryKey: ["release-templates"] });
  };

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not do that.");
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = () =>
    void run(() =>
      api.patch(`/api/v1/release-template-versions/${version!.id}`, {
        changeSummary: settings.changeSummary.trim() === "" ? null : settings.changeSummary.trim(),
        rollbackRequired: settings.rollbackRequired,
        observationMinutes:
          settings.observationMinutes === "" ? null : Number(settings.observationMinutes),
        lockVersion: version!.lockVersion,
      }),
    );

  /*
   * Discarding is offered only for a draft, and only when it is not the only version — a
   * template with no version cannot be used to create anything, and deleting its last draft
   * would quietly take it out of service.
   */
  const discardDraft = () => {
    if (!window.confirm("Discard this draft? Everything defined in it goes with it.")) {
      return;
    }
    void run(async () => {
      await api.del(`/api/v1/release-template-versions/${version!.id}`);
      setSelectedId(null);
    });
  };

  const newDraft = () =>
    void run(async () => {
      const created = await api.post<ReleaseTemplateVersion>(
        `/api/v1/release-templates/${template.id}/versions`,
        { changeSummary: null },
      );
      setSelectedId(created.id);
    });

  const activate = () =>
    void run(() =>
      api.post(`/api/v1/release-template-versions/${version!.id}/activate`, {
        lockVersion: version!.lockVersion,
      }),
    );

  const removeContent = (kind: ContentKind, contentId: string) =>
    void run(() =>
      api.del(
        `/api/v1/release-template-versions/${version!.id}/contents/${kind}/${contentId}`,
      ),
    );

  const tabs: TabDefinition<ContentKind>[] = [
    { key: "prerequisites", label: "Prerequisites", badge: version?.prerequisites.length || undefined },
    { key: "approvals", label: "Approvals", badge: version?.approvals.length || undefined },
    { key: "execution-steps", label: "Runbook", badge: version?.executionSteps.length || undefined },
    { key: "rollback-steps", label: "Rollback", badge: version?.rollbackSteps.length || undefined },
    { key: "validation-checks", label: "Validation", badge: version?.validationChecks.length || undefined },
    { key: "test-requirements", label: "Testing", badge: version?.testRequirements.length || undefined },
    { key: "document-requirements", label: "Documents", badge: version?.documentRequirements.length || undefined },
  ];

  return (
    <Drawer
      open
      onClose={onClose}
      title={template.name}
      subtitle={template.code}
      // Seven content tabs plus their badges do not fit a narrower panel; the ones that fall off
      // the right-hand edge read as though the version had nothing in them.
      width="xl"
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {can("RELEASE_TEMPLATE_MANAGE") && (
            <div className="flex gap-2">
              {version?.status === "DRAFT" ? (
                <>
                  {(versions.data ?? []).length > 1 && (
                    <Button variant="ghost" onClick={discardDraft} loading={busy}>
                      Discard draft
                    </Button>
                  )}
                  <Button onClick={activate} loading={busy}>
                    Activate V{version.versionNumber}
                  </Button>
                </>
              ) : (
                <Button variant="secondary" onClick={newDraft} loading={busy}>
                  New draft
                </Button>
              )}
            </div>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {versions.isPending ? (
          <LoadingState label="Loading versions…" />
        ) : (
          <>
            <Field label="Version">
              <Select
                value={selectedId ?? ""}
                onChange={(event) => setSelectedId(event.target.value)}
              >
                {(versions.data ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    V{entry.versionNumber} — {VERSION_STATUS_LABELS[entry.status]}
                  </option>
                ))}
              </Select>
            </Field>

            {version && (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={VERSION_STATUS_TONE[version.status]}>
                    {VERSION_STATUS_LABELS[version.status]}
                  </StatusBadge>
                  <span className="text-xs text-ink-500">
                    {describeVersionContents({
                      prerequisites: version.prerequisites.length,
                      approvals: version.approvals.length,
                      executionSteps: version.executionSteps.length,
                      validationChecks: version.validationChecks.length,
                    })}
                  </span>
                </div>

                {version.status !== "DRAFT" && (
                  <InlineAlert tone="info">
                    <p className="flex items-start gap-1.5">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                      <span>
                        This version is {VERSION_STATUS_LABELS[version.status].toLowerCase()} and
                        cannot be changed. {describeImpact(version.releasesBuiltFrom)} To change
                        what future releases get, create a new draft.
                      </span>
                    </p>
                  </InlineAlert>
                )}

                {version.changeSummary && (
                  <p className="rounded-md bg-ink-50 px-3 py-2 text-sm text-ink-700">
                    {version.changeSummary}
                  </p>
                )}

                {/*
                  Editable only while the version is a draft. §52 makes an activated version
                  immutable, and a form that silently did nothing would be worse than none.
                */}
                {editable ? (
                  <div className="space-y-3 rounded-lg border border-ink-200 p-3">
                    <p className="text-sm font-medium text-ink-900">What this version requires</p>
                    <Field label="What changed in this version">
                      <Input
                        value={settings.changeSummary}
                        placeholder="Added a security approval for production."
                        onChange={(event) =>
                          setSettings({ ...settings, changeSummary: event.target.value })
                        }
                      />
                    </Field>
                    <label className="flex items-start gap-2 text-sm text-ink-800">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-ink-300"
                        checked={settings.rollbackRequired}
                        onChange={(event) =>
                          setSettings({ ...settings, rollbackRequired: event.target.checked })
                        }
                      />
                      <span>
                        A rollback plan is required
                        <span className="block text-xs text-ink-500">
                          Releases built from this fail their rollback gate without one.
                        </span>
                      </span>
                    </label>
                    <Field
                      label="Observation window (minutes)"
                      hint="How long a release must be watched after deployment before it can be completed. Blank for none."
                    >
                      <Input
                        type="number"
                        min={0}
                        value={settings.observationMinutes}
                        onChange={(event) =>
                          setSettings({ ...settings, observationMinutes: event.target.value })
                        }
                      />
                    </Field>
                    <div className="flex justify-end">
                      <Button size="sm" loading={busy} onClick={saveSettings}>
                        Save settings
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg bg-ink-50 px-3 py-2">
                      <p className="text-xs text-ink-500">Rollback plan</p>
                      <p className="text-ink-900">
                        {version.rollbackRequired ? "Required" : "Not required"}
                      </p>
                    </div>
                    <div className="rounded-lg bg-ink-50 px-3 py-2">
                      <p className="text-xs text-ink-500">Observation window</p>
                      <p className="text-ink-900">
                        {version.observationMinutes == null
                          ? "None"
                          : `${version.observationMinutes} minutes`}
                      </p>
                    </div>
                  </div>
                )}

                <Tabs tabs={tabs} active={tab} onChange={setTab} />

                <div className="space-y-1.5">
                  {contentsOf(version, tab).length === 0 ? (
                    <p className="py-4 text-center text-sm text-ink-500">
                      Nothing defined for this yet.
                    </p>
                  ) : (
                    contentsOf(version, tab).map((row) => (
                      <div
                        key={row.id}
                        className="flex items-start justify-between gap-3 rounded-lg border border-ink-200 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-sm text-ink-900">{row.label}</p>
                          {row.detail && (
                            <p className="mt-0.5 text-xs text-ink-500">{row.detail}</p>
                          )}
                        </div>
                        {editable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={`Remove ${row.label}`}
                            onClick={() => removeContent(tab, row.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}

                  {editable && (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={() => setAdding(tab)}
                    >
                      Add
                    </Button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <TemplateContentDrawer
        versionId={version?.id ?? null}
        kind={adding}
        onClose={() => setAdding(null)}
        onSaved={refresh}
      />
    </Drawer>
  );
}

/** One shape for every content tab, so the list below does not need five branches. */
function contentsOf(
  version: ReleaseTemplateVersion,
  kind: ContentKind,
): { id: string; label: string; detail?: string }[] {
  switch (kind) {
    case "prerequisites":
      return version.prerequisites.map((row) => ({
        id: row.id,
        label: row.title,
        detail: `${row.category.replace(/_/g, " ").toLowerCase()}${row.mandatory ? "" : " · optional"}${row.evidenceRequired ? " · evidence required" : ""}`,
      }));
    case "approvals":
      return version.approvals.map((row) => ({
        id: row.id,
        label: row.roleLabel,
        detail: row.mandatory ? "required" : "optional",
      }));
    case "execution-steps":
      return version.executionSteps.map((row) => ({
        id: row.id,
        label: `${row.sequenceNo}. ${row.title}`,
        detail: `${row.phase.toLowerCase()} · ${row.stepType.toLowerCase()}${row.mandatory ? "" : " · optional"}`,
      }));
    case "rollback-steps":
      return version.rollbackSteps.map((row) => ({
        id: row.id,
        label: `${row.sequenceNo}. ${row.title}`,
        detail: row.estimatedMinutes == null ? undefined : `${row.estimatedMinutes} minutes`,
      }));
    case "validation-checks":
      return version.validationChecks.map((row) => ({
        id: row.id,
        label: row.checkName,
        detail: `${row.category.replace(/_/g, " ").toLowerCase()}${row.mandatory ? "" : " · optional"}`,
      }));
    case "test-requirements":
      return version.testRequirements.map((row) => ({
        id: row.id,
        label: row.category.replace(/_/g, " ").toLowerCase(),
        detail: row.required ? "required" : "optional",
      }));
    case "document-requirements":
      return version.documentRequirements.map((row) => ({
        id: row.id,
        label: row.documentType.replace(/_/g, " ").toLowerCase(),
        detail: row.mandatory ? "required" : "optional",
      }));
    default:
      return [];
  }
}
